import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { computeNailPositions, buildErrorMap, renderSequence, drawImageFit } from "@/lib/stringArt";
import { generateMono } from "@/lib/generator";

const W = 680;
const H = 680;

// Interactive before -> after slider. The "after" is REAL string art generated
// from the example photo (client-side, cached). Both images are rendered to
// canvases with a white board so they display with true colours under the
// app's global luminance-invert theme (the CSS auto re-inverts <img>).
export default function BeforeAfterSlider({ src, afterSrc = null, label = "", numLines = 2000, lineOpacity = 0.16, lineWeight = 16 }) {
  const [beforeUrl, setBeforeUrl] = useState(null);
  const [afterUrl, setAfterUrl] = useState(afterSrc);
  const [pos, setPos] = useState(55);
  const [loading, setLoading] = useState(!afterSrc);
  const wrapRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `ba4:${src}:${numLines}:${lineOpacity}:${lineWeight}`;

    const run = async () => {
      const img = new Image();
      img.src = src;
      await img.decode().catch(() => {});
      if (cancelled) return;

      // "before" — photo on a white circular board (matches the art framing)
      const bc = document.createElement("canvas");
      bc.width = W;
      bc.height = H;
      const bx = bc.getContext("2d");
      bx.fillStyle = "#ffffff";
      bx.fillRect(0, 0, W, H);
      bx.save();
      bx.beginPath();
      bx.arc(W / 2, H / 2, W / 2 - 2, 0, Math.PI * 2);
      bx.clip();
      try {
        drawImageFit(bx, img, W, H, "cover", "#ffffff", 1);
      } catch (e) {
        /* ignore */
      }
      bx.restore();
      if (!cancelled) setBeforeUrl(bc.toDataURL("image/jpeg", 0.85));

      // "after" — arte pré-gerada no servidor (qualidade garantida, sem espera)
      if (afterSrc) {
        if (!cancelled) {
          setAfterUrl(afterSrc);
          setLoading(false);
        }
        return;
      }

      // sem arte pré-gerada: gera no browser com o motor real (com cache)
      let url = null;
      try {
        url = localStorage.getItem(cacheKey);
      } catch (e) {
        /* ignore */
      }
      if (!url) {
        const nails = computeNailPositions("circle", 240, W, H, 3, 1, 1);
        const error = buildErrorMap(img, "circle", W, H, false, "cover", 1, nails);
        const seq = await generateMono({ error, nails, W, H, numLines, lineWeight, lengthPenalty: 0.12 }, () => {});
        if (cancelled) return;
        const c = document.createElement("canvas");
        c.width = W;
        c.height = H;
        renderSequence(c, seq, nails, W, H, { threadColor: "#111111", bgColor: "#ffffff", lineOpacity, showNails: false });
        url = c.toDataURL("image/jpeg", 0.85);
        try {
          localStorage.setItem(cacheKey, url);
        } catch (e) {
          /* ignore */
        }
      }
      if (!cancelled) {
        setAfterUrl(url);
        setLoading(false);
      }
    };

    const t = setTimeout(run, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [src, afterSrc, numLines, lineOpacity, lineWeight]);

  const updateFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(2, Math.min(98, p)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      updateFromClientX(e.clientX);
    };
    const onUp = () => {
      draggingRef.current = false;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [updateFromClientX]);

  const onDown = (e) => {
    draggingRef.current = true;
    updateFromClientX(e.clientX);
  };

  return (
    <div className="w-full">
      <div
        ref={wrapRef}
        onPointerDown={onDown}
        className="relative aspect-square w-full overflow-hidden rounded-xl border border-black/10 shadow-xl select-none touch-none cursor-ew-resize bg-[#0a0a0a]"
      >
        {/* base: original photo (before) — <img> auto shows true colours */}
        {beforeUrl && <img src={beforeUrl} alt="Foto original" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />}

        {/* overlay: string art (after), revealed from the left up to pos% */}
        {afterUrl && (
          <img
            src={afterUrl}
            alt="String art"
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          />
        )}

        {/* UI overlay (true colours via data-no-invert; no <img> inside) */}
        <div data-no-invert className="absolute inset-0 pointer-events-none">
          <span className="absolute top-3 left-3 bg-black/75 text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded">String art</span>
          <span className="absolute top-3 right-3 bg-white/85 text-black text-[10px] uppercase tracking-wider px-2 py-1 rounded">Foto</span>

          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF3B00]" />
              <span className="text-xs mt-2">A gerar a string art…</span>
            </div>
          )}

          {!loading && (
            <div className="absolute top-0 bottom-0" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
              <div className="w-0.5 h-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-lg border border-black/10 flex items-center justify-center">
                <span className="text-[#FF3B00] text-sm font-bold">↔</span>
              </div>
            </div>
          )}
        </div>
      </div>
      {label && <p className="mt-3 text-center text-xs uppercase tracking-wider text-[#8B8B93]">{label}</p>}
    </div>
  );
}
